import { Preferences } from '@capacitor/preferences';
import axios from 'axios';

const DEVICE_ID_KEY = 'messenger-e2e-device-id';
const PRIVATE_JWK_KEY = 'messenger-e2e-private-jwk';
const PUBLIC_JWK_KEY = 'messenger-e2e-public-jwk';
const chatKeyCache = new Map<number, CryptoKey>();

type JsonWebKeyLocal = JsonWebKey;

type WrappedChatKeyPayload = {
  iv: string;
  data: string;
  /** ECDH-обёртка для чужого устройства */
  senderPublic?: string;
  /** AES-обёртка для своего устройства (без ECDH сам-с-собой) */
  self?: boolean;
};

function chatKeyStorageKey(chatId: number): string {
  return `messenger-e2e-chat-key-${chatId}`;
}

function b64Encode(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  arr.forEach(b => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function b64Decode(value: string): ArrayBuffer {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

async function importPublicKey(jwk: JsonWebKeyLocal): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, true, []);
}

async function importPrivateKey(jwk: JsonWebKeyLocal): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
}

async function deriveWrapKey(privateKey: CryptoKey, publicKey: CryptoKey): Promise<CryptoKey> {
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
  return crypto.subtle.importKey('raw', bits, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** Обёртка ключа чата для своего устройства: ECDH(priv, ownPub) в WebCrypto часто падает. */
async function deriveSelfWrapKey(privateJwkRaw: string): Promise<CryptoKey> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`liga-e2e-self:${privateJwkRaw}`));
  return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

async function importChatKeyRaw(raw: ArrayBuffer): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

async function cacheChatKey(chatId: number, key: CryptoKey): Promise<void> {
  chatKeyCache.set(chatId, key);
  try {
    const raw = await crypto.subtle.exportKey('raw', key);
    await Preferences.set({ key: chatKeyStorageKey(chatId), value: b64Encode(raw) });
  } catch (error) {
    console.warn('Failed to persist chat key locally', error);
  }
}

async function loadCachedChatKey(chatId: number): Promise<CryptoKey | null> {
  const mem = chatKeyCache.get(chatId);
  if (mem) return mem;
  const stored = (await Preferences.get({ key: chatKeyStorageKey(chatId) })).value;
  if (!stored) return null;
  try {
    const key = await importChatKeyRaw(b64Decode(stored));
    chatKeyCache.set(chatId, key);
    return key;
  } catch {
    return null;
  }
}

export async function ensureMessengerCryptoReady(): Promise<{ deviceId: string; publicJwk: JsonWebKeyLocal }> {
  let deviceId = (await Preferences.get({ key: DEVICE_ID_KEY })).value;
  let publicRaw = (await Preferences.get({ key: PUBLIC_JWK_KEY })).value;
  let privateRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;

  if (!deviceId || !publicRaw || !privateRaw) {
    deviceId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const pair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
    const publicJwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
    const privateJwk = await crypto.subtle.exportKey('jwk', pair.privateKey);
    publicRaw = JSON.stringify(publicJwk);
    privateRaw = JSON.stringify(privateJwk);
    await Preferences.set({ key: DEVICE_ID_KEY, value: deviceId });
    await Preferences.set({ key: PUBLIC_JWK_KEY, value: publicRaw });
    await Preferences.set({ key: PRIVATE_JWK_KEY, value: privateRaw });
  }

  const publicJwk = JSON.parse(publicRaw) as JsonWebKeyLocal;
  await axios.post('api/messenger/devices', {
    deviceId,
    identityKey: publicRaw,
  });
  return { deviceId, publicJwk };
}

async function loadPrivateKey(): Promise<CryptoKey> {
  const privateRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
  if (!privateRaw) throw new Error('E2E private key missing');
  return importPrivateKey(JSON.parse(privateRaw));
}

async function wrapChatKeyForDevice(params: {
  chatKey: CryptoKey;
  privateKey: CryptoKey;
  privateJwkRaw: string;
  myDeviceId: string;
  myPublicRaw: string;
  device: { userId: number; deviceId: string; wrappedKey: string };
}): Promise<{ userId: number; deviceId: string; wrappedKey: string }> {
  const rawChat = await crypto.subtle.exportKey('raw', params.chatKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  if (params.device.deviceId === params.myDeviceId) {
    const wrapKey = await deriveSelfWrapKey(params.privateJwkRaw);
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, rawChat);
    return {
      userId: params.device.userId,
      deviceId: params.device.deviceId,
      wrappedKey: JSON.stringify({
        self: true,
        iv: b64Encode(iv),
        data: b64Encode(encrypted),
      } as WrappedChatKeyPayload),
    };
  }

  const theirPub = await importPublicKey(JSON.parse(params.device.wrappedKey));
  const wrapKey = await deriveWrapKey(params.privateKey, theirPub);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, rawChat);
  return {
    userId: params.device.userId,
    deviceId: params.device.deviceId,
    wrappedKey: JSON.stringify({
      iv: b64Encode(iv),
      data: b64Encode(encrypted),
      senderPublic: params.myPublicRaw,
    } as WrappedChatKeyPayload),
  };
}

async function unwrapChatKey(wrappedPayload: string): Promise<CryptoKey> {
  const payload = JSON.parse(wrappedPayload) as WrappedChatKeyPayload;
  if (payload.self) {
    const privateRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
    if (!privateRaw) throw new Error('E2E private key missing');
    const wrapKey = await deriveSelfWrapKey(privateRaw);
    const raw = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64Decode(payload.iv) },
      wrapKey,
      b64Decode(payload.data)
    );
    return importChatKeyRaw(raw);
  }
  if (!payload.senderPublic) throw new Error('E2E wrapped key missing senderPublic');
  const privateKey = await loadPrivateKey();
  const senderPub = await importPublicKey(JSON.parse(payload.senderPublic));
  const wrapKey = await deriveWrapKey(privateKey, senderPub);
  const raw = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64Decode(payload.iv) },
    wrapKey,
    b64Decode(payload.data)
  );
  return importChatKeyRaw(raw);
}

async function loadExistingChatKey(chatId: number): Promise<CryptoKey | null> {
  const cached = await loadCachedChatKey(chatId);
  if (cached) return cached;

  const { deviceId } = await ensureMessengerCryptoReady();
  const { data: myKeys } = await axios.get<Array<{ wrappedKey: string; deviceId: string }>>(
    `api/messenger/chats/${chatId}/keys`,
    { params: { deviceId } }
  );
  const mine = (myKeys || []).find(item => item.deviceId === deviceId);
  if (!mine?.wrappedKey) return null;
  const key = await unwrapChatKey(mine.wrappedKey);
  await cacheChatKey(chatId, key);
  return key;
}

export async function ensureChatKey(chatId: number): Promise<CryptoKey> {
  const existing = await loadExistingChatKey(chatId);
  if (existing) return existing;

  const { deviceId: myDeviceId } = await ensureMessengerCryptoReady();
  const privateJwkRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
  const myPublicRaw = (await Preferences.get({ key: PUBLIC_JWK_KEY })).value;
  if (!privateJwkRaw || !myPublicRaw) throw new Error('E2E keys missing');

  const chatKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const { data: devices } = await axios.get<Array<{ userId: number; deviceId: string; wrappedKey: string }>>(
    `api/messenger/chats/${chatId}/device-keys`
  );
  const privateKey = await loadPrivateKey();
  const keys: Array<{ userId: number; deviceId: string; wrappedKey: string }> = [];
  let wrappedForSelf = false;

  for (const device of devices || []) {
    try {
      const wrapped = await wrapChatKeyForDevice({
        chatKey,
        privateKey,
        privateJwkRaw,
        myDeviceId,
        myPublicRaw,
        device,
      });
      keys.push(wrapped);
      if (device.deviceId === myDeviceId) wrappedForSelf = true;
    } catch (error) {
      console.warn('Skip wrapping chat key for device', device.deviceId, error);
    }
  }

  if (!wrappedForSelf) {
    throw new Error('Не удалось сохранить ключ шифрования для этого устройства');
  }
  if (keys.length) {
    await axios.put(`api/messenger/chats/${chatId}/keys`, { keys });
  }
  await cacheChatKey(chatId, chatKey);
  return chatKey;
}

export async function encryptMessageBody(chatId: number, body: string): Promise<string> {
  const key = await ensureChatKey(chatId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(body || '');
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  return JSON.stringify({ iv: b64Encode(iv), data: b64Encode(cipher) });
}

export async function decryptMessageBody(chatId: number, ciphertext: string | null | undefined): Promise<string | null> {
  if (!ciphertext) return null;
  try {
    const payload = JSON.parse(ciphertext) as { iv: string; data: string };
    const key = await loadExistingChatKey(chatId);
    if (!key) return '🔒 Нет ключа шифрования';
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64Decode(payload.iv) },
      key,
      b64Decode(payload.data)
    );
    return new TextDecoder().decode(plain);
  } catch (error) {
    console.warn('E2E decrypt failed', error);
    return '🔒 Не удалось расшифровать';
  }
}

export function isE2EChatType(type?: string): boolean {
  return type === 'DIRECT' || type === 'GROUP' || type === 'PUBLIC';
}
